import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Globe,
  Palette,
  Mail,
  MessageSquare,
  MessageCircle,
  FileText,
  Users,
  UserCog,
  UserPlus,
  Plug,
  BookOpen,
  MapPin,
  Library,
  Tag,
  Hash,
  ClipboardCheck,
  CheckCircle,
  Scan,
  Upload,
  Search,
  ArrowRight,
  Settings,
  Sparkles,
  Building2,
  Briefcase,
  Building,
  Shield,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface SettingsCategory {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  category: "foundation" | "communication" | "catalog" | "staff" | "patrons" | "advanced";
  comingSoon?: boolean;
  color?: string;
}

const settingsCategories: SettingsCategory[] = [
  // Foundation
  {
    id: "domain",
    title: "Domain Setup",
    description: "Configure your library's custom domain or subdomain",
    icon: Globe,
    path: "/setup/domain",
    category: "foundation",
    color: "primary",
  },
  {
    id: "branding",
    title: "Branding",
    description: "Customize logo, colors, and visual identity",
    icon: Palette,
    path: "/setup/branding",
    category: "foundation",
    color: "primary",
  },
  {
    id: "general-policies",
    title: "General Policies",
    description: "Borrowing limits, renewal rules, and fine policies",
    icon: FileText,
    path: "/setup/general-policies",
    category: "foundation",
    color: "primary",
  },
  {
    id: "circulation-policies",
    title: "Circulation Policies",
    description: "Loan rules per patron type and item type",
    icon: BookOpen,
    path: "/setup/circulation-policies",
    category: "foundation",
    color: "primary",
  },
  // Communication
  {
    id: "email-templates",
    title: "Email Templates",
    description: "Customize automated email notifications",
    icon: Mail,
    path: "/setup/email-templates",
    category: "communication",
    color: "primary",
  },
  {
    id: "sms-templates",
    title: "SMS Templates",
    description: "Manage SMS alerts and reminders",
    icon: MessageSquare,
    path: "/setup/sms-templates",
    category: "communication",
    color: "primary",
  },
  {
    id: "welcome-message",
    title: "Welcome Message",
    description: "Set the message for new users",
    icon: MessageCircle,
    path: "/setup/welcome-message",
    category: "communication",
    color: "primary",
  },
  // Catalog
  {
    id: "item-types",
    title: "Item Types",
    description: "Define types of library materials",
    icon: BookOpen,
    path: "/setup/item-types",
    category: "catalog",
    color: "primary",
  },
  {
    id: "locations",
    title: "Locations",
    description: "Manage physical storage locations",
    icon: MapPin,
    path: "/setup/locations",
    category: "catalog",
    color: "primary",
  },
  {
    id: "collections",
    title: "Collections",
    description: "Manage special collections and groupings",
    icon: Library,
    path: "/setup/collections",
    category: "catalog",
    color: "primary",
  },
  {
    id: "book-conditions",
    title: "Book Conditions",
    description: "Physical condition descriptions",
    icon: Tag,
    path: "/setup/book-conditions",
    category: "catalog",
    color: "primary",
  },
  {
    id: "item-statuses",
    title: "Item Statuses",
    description: "View system-defined availability statuses",
    icon: CheckCircle,
    path: "/setup/item-statuses",
    category: "catalog",
    color: "primary",
  },
  {
    id: "barcode-pattern",
    title: "Barcode Pattern",
    description: "Configure auto-generated barcode format",
    icon: Tag,
    path: "/setup/barcode-pattern",
    category: "catalog",
    color: "primary",
  },
  {
    id: "accession-number",
    title: "Accession Numbers",
    description: "Configure accession number generation mode",
    icon: Hash,
    path: "/setup/accession-number",
    category: "catalog",
    color: "primary",
  },
  {
    id: "catalog-approval",
    title: "Catalog approval",
    description: "Publish immediately, or require the review queue",
    icon: ClipboardCheck,
    path: "/setup/catalog-approval",
    category: "catalog",
    color: "primary",
  },
  // Staff Management
  {
    id: "staff",
    title: "Staff",
    description: "Manage library staff members, invitations, and status",
    icon: Users,
    path: "/setup/staff",
    category: "staff",
    color: "primary",
  },
  {
    id: "departments",
    title: "Departments",
    description: "Organize staff by organizational departments",
    icon: Building2,
    path: "/setup/departments",
    category: "staff",
    color: "primary",
  },
  {
    id: "job-titles",
    title: "Job Titles",
    description: "Define professional job titles for staff",
    icon: Briefcase,
    path: "/setup/job-titles",
    category: "staff",
    color: "primary",
  },
  {
    id: "branches",
    title: "Branches",
    description: "Manage library branches and locations",
    icon: Building,
    path: "/setup/branches",
    category: "staff",
    color: "primary",
  },
  {
    id: "roles",
    title: "Roles & Permissions",
    description: "Define roles and assign permissions to staff",
    icon: Shield,
    path: "/setup/roles",
    category: "staff",
    color: "primary",
  },
  {
    id: "staff-custom-fields",
    title: "Staff Custom Fields",
    description: "Configure custom fields for staff member creation",
    icon: FileText,
    path: "/setup/staff-custom-fields",
    category: "staff",
    color: "primary",
  },
  // Patron Setup
  {
    id: "patron-types",
    title: "Patron Types & Roles",
    description: "Define patron categories and privileges",
    icon: UserCog,
    path: "/setup/patron-types",
    category: "patrons",
    color: "primary",
  },
  {
    id: "registration",
    title: "Registration Setup",
    description: "Configure patron registration forms",
    icon: UserPlus,
    path: "/setup/registration",
    category: "patrons",
    color: "primary",
  },
  // Advanced
  {
    id: "integrations",
    title: "Integrations",
    description: "External integrations and APIs",
    icon: Plug,
    path: "/setup/integrations",
    category: "advanced",
    color: "primary",
  },
];

const categoryLabels: Record<string, string> = {
  foundation: "Foundation",
  communication: "Communication",
  catalog: "Catalog Management",
  staff: "Staff Management",
  patrons: "Patron Setup",
  advanced: "Advanced",
};

const categoryIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  foundation: Settings,
  communication: Mail,
  catalog: BookOpen,
  staff: Users,
  patrons: UserCog,
  advanced: Sparkles,
};

const SettingsOverviewPage = () => {
  const navigate = useNavigate();
  const { isTenantAdmin } = useAccessControl();
  const [searchQuery, setSearchQuery] = useState("");

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="p-8 text-center">
              <h2 className="text-xl font-heading font-bold text-[#495057] mb-2">Access Denied</h2>
              <p className="text-[#495057] opacity-70">
                You need Tenant Admin permissions to access this page.
              </p>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  const handleCardClick = (category: SettingsCategory) => {
    if (!category.comingSoon) {
      navigate(category.path);
    }
  };

  // Filter categories by search query
  const filteredCategories = settingsCategories.filter(
    (category) =>
      category.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      category.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group categories by category type
  const groupedCategories = filteredCategories.reduce(
    (acc, category) => {
      if (!acc[category.category]) {
        acc[category.category] = [];
      }
      acc[category.category].push(category);
      return acc;
    },
    {} as Record<string, SettingsCategory[]>
  );

  return (
    <AppLayout>
      <PageContainer width="wide">
        <PageHeader
          title="Library Setup"
          description="Personalize your library space. Configure settings and preferences to make your library management system work exactly how you need it."
          breadcrumbItems={[{ label: "Setup" }]}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search settings..."
        />

        {/* Settings Content */}
        <div className="space-y-8">
          {/* Settings by Category */}
          {Object.entries(groupedCategories).map(([categoryKey, categories], index) => {
            const CategoryIcon = categoryIcons[categoryKey];
            const isEven = index % 2 === 0;

            return (
              <div
                key={categoryKey}
                className={cn(
                  "py-6 px-4 rounded-2xl transition-all",
                  isEven
                    ? "bg-white shadow-sm"
                    : "bg-gradient-to-br from-white to-[#F8F9FA] shadow-sm"
                )}
              >
                {/* Section Header */}
                <div className="flex items-center gap-3 pb-3 mb-6 border-b border-gray-100">
                  <div className="p-2.5 bg-primary/10 rounded-xl shadow-sm">
                    <CategoryIcon className="h-5 w-5 text-primary" />
                  </div>
                  <h2 className="text-xl font-heading font-semibold text-[#495057]">
                    {categoryLabels[categoryKey]}
                  </h2>
                  <Badge className="ml-auto bg-primary text-primary-foreground shadow-sm">
                    {categories.length}
                  </Badge>
                </div>

                {/* Category Cards Grid */}
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                  {categories.map((category) => {
                    const Icon = category.icon;
                    return (
                      <Card
                        key={category.id}
                        onClick={() => handleCardClick(category)}
                        className={cn(
                          "group cursor-pointer transition-all duration-300 h-full flex flex-col",
                          "bg-white border border-gray-200/60",
                          "hover:shadow-xl hover:-translate-y-1 hover:border-primary/30",
                          "shadow-md",
                          category.comingSoon ? "opacity-60 cursor-not-allowed" : ""
                        )}
                      >
                        <CardHeader className="p-5 pb-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="p-3 rounded-xl bg-primary/10 transition-all duration-300 group-hover:scale-110 group-hover:bg-primary/15 shadow-sm">
                              <Icon className="h-5 w-5 text-primary" />
                            </div>
                            {category.comingSoon && (
                              <Badge variant="secondary" className="text-xs">
                                Coming Soon
                              </Badge>
                            )}
                          </div>
                          <CardTitle className="text-lg mb-2 text-[#495057] group-hover:text-primary transition-colors duration-200 font-semibold">
                            {category.title}
                          </CardTitle>
                          <CardDescription className="text-sm text-[#495057] opacity-75 line-clamp-2 leading-relaxed">
                            {category.description}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="px-5 pt-0 pb-5 mt-auto">
                          {!category.comingSoon && (
                            <div className="flex items-center gap-2 text-primary font-semibold text-sm group-hover:gap-3 transition-all duration-200">
                              <span>Configure</span>
                              <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Empty State */}
          {Object.keys(groupedCategories).length === 0 && (
            <Card className="py-16 text-center bg-white shadow-sm">
              <CardContent>
                <div className="p-4 bg-[#F8F9FA] rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                  <Search className="h-8 w-8 text-[#495057] opacity-40" />
                </div>
                <h3 className="text-xl font-semibold text-[#495057] mb-2">No settings found</h3>
                <p className="text-sm text-[#495057] opacity-70">Try adjusting your search query</p>
              </CardContent>
            </Card>
          )}
        </div>
      </PageContainer>
    </AppLayout>
  );
};

export default SettingsOverviewPage;
